<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Consignment Wise Profit/Loss Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Filters -->
                <v-row class="py-2">
                  <v-col md="3" cols="12">
                    <v-autocomplete
                      v-model="filters.container_no"
                      label="Container No"
                      :items="containerNoOptions"
                      item-title="container_no"
                      item-value="container_no"
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      clearable
                      hide-details="auto"
                      placeholder="Select container"
                    />
                  </v-col>
                  <v-col md="2" cols="12">
                    <v-date-input
                      input-format="DD-MM-YYYY"
                      v-model="filters.from_date"
                      label="From Date"
                      density="compact"
                      prepend-inner-icon="mdi-calendar-start"
                      variant="outlined"
                      prepend-icon=""
                      bg-color="grey-lighten-4"
                      autocomplete="off"
                      clearable
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col md="2" cols="12">
                    <v-date-input
                      input-format="DD-MM-YYYY"
                      v-model="filters.to_date"
                      label="To Date"
                      density="compact"
                      prepend-inner-icon="mdi-calendar-end"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      autocomplete="off"
                      clearable
                      prepend-icon=""
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col md="5" cols="12" class="d-flex align-center" style="gap: 12px;">
                    <v-btn class="bg-brand" @click="loadReport" :loading="loading">
                      <v-icon left>mdi-magnify</v-icon>
                      Generate Report
                    </v-btn>
                    <v-btn variant="outlined" color="warning" prepend-icon="mdi-refresh" @click="resetFilters">
                      Reset
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Report content (printable) -->
                <v-sheet
                  v-if="reportData.length > 0"
                  class="report-paper pa-5 mx-auto mt-4"
                  elevation="3"
                  id="printable-area"
                >
                  <div class="report-title-box mb-4">
                    <h2 class="report-title text-center font-weight-bold">Consignment Wise Profit/Loss Report</h2>
                    <div class="d-flex justify-space-between mt-2 px-2">
                    
                      
                    </div>
                  </div>

                  <!-- 1. Consignment Details Table -->
                  <div class="table-container mb-6">
                    <table class="dep-table">
                      <thead>
                        <tr>
                          <th class="text-center" style="width: 40px;">SL</th>
                          <th>SO No</th>
                          <th>Bill No</th>
                          <th>Container No</th>
                          <th>Buyer Name</th>
                          <th class="text-end">Bill Amount</th>
                          <th class="text-end">Receive Amount</th>
                          <th class="text-end">Discount Amount</th>
                          <th class="text-end">Due Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, index) in reportData" :key="row.id">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>{{ (row.sale_order || row.saleOrder)?.code || '—' }}</td>
                          <td>{{ row.code || '—' }}</td>
                          <td>{{ row.container_no || '—' }}</td>
                          <td>{{ row.customer?.customer_name || row.customer?.customer_owner_name || '—' }}</td>
                          <td class="text-end">{{ formatAmount(row.total) }}</td>
                          <td class="text-end">{{ formatAmount(row.paid) }}</td>
                          <td class="text-end">{{ formatAmount(row.discount_amount) }}</td>
                          <td class="text-end">{{ formatAmount(row.due) }}</td>
                        </tr>
                        <tr class="total-row">
                          <td style="border-right: none !important;"></td>
                          <td style="border-left: none !important; border-right: none !important;"></td>
                          <td style="border-left: none !important; border-right: none !important;"></td>
                          <td style="border-left: none !important;"></td>
                          <td class="font-weight-bold" style="border-left: none !important;">Total</td>
                          <td class="text-end font-weight-bold">{{ formatAmount(totals.totalBill) }}</td>
                          <td class="text-end font-weight-bold">{{ formatAmount(totals.totalReceive) }}</td>
                          <td class="text-end font-weight-bold">{{ formatAmount(totals.totalDiscount) }}</td>
                          <td class="text-end font-weight-bold">{{ formatAmount(totals.totalDue) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- 2. Particulars / Expenses Table (includes summary rows) -->
                  <div class="table-container mb-6">
                    <table class="dep-table">
                      <thead>
                        <tr>
                          <th style="width: 120px;">Date</th>
                          <th>Particulars</th>
                          <th class="text-end" style="width: 140px;">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(p, index) in particularsList" :key="index">
                          <td>{{ formatDateView(p.voucher_date) }}</td>
                          <td>{{ p.particulars }}</td>
                          <td class="text-end">{{ formatAmount(p.amount) }}</td>
                        </tr>
                        <tr class="total-row">
                          <td style="border-right: none !important;"></td>
                          <td class="font-weight-bold text-end" style="border-left: none !important;">Total</td>
                          <td class="text-end font-weight-bold" >{{ formatAmount(totals.totalExpenses) }}</td>
                        </tr>
                        <tr>
                          <td style="border-right: none !important;"></td>
                          <td class="text-end" style="border-left: none !important;">Total Bill Amount</td>
                          <td class="text-end font-weight-bold">{{ formatAmount(totals.totalBill) }}</td>
                        </tr>
                        <tr>
                          <td style="border-right: none !important;"></td>
                          <td class="text-end" style="border-left: none !important;">(-)Expenses Amount</td>
                          <td class="text-end font-weight-bold">{{ formatAmount(totals.totalExpenses) }}</td>
                        </tr>
                        <tr>
                          <td style="border-right: none !important;"></td>
                          <td class="text-end" style="border-left: none !important;">(-)Discount Amount</td>
                          <td class="text-end font-weight-bold">{{ formatAmount(totals.totalDiscount) }}</td>
                        </tr>
                        <tr >
                          <td style="border-right: none !important;"></td>
                          <td class="text-end" style="border-left: none !important;">Profit/Loss Amount</td>
                          <td class="text-end font-weight-bold">{{ formatAmount(totals.profitLoss) }}</td>
                        </tr>
                        <tr>
                          <td style="border-right: none !important;"></td>
                          <td class="text-end" style="border-left: none !important;">Total Due Amount</td>
                          <td class="text-end font-weight-bold">{{ formatAmount(totals.totalDue) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- 3. In Word (left) & Authorized Signature (bottom right) -->
                  <div class="d-flex justify-space-between align-end flex-wrap mt-6 signature-section">
                    <div class="in-word-text">
                      <strong>In Word:</strong> {{ amountInWords }}
                    </div>
                    <div class="authorized-signature">Authorized Signature</div>
                  </div>
                </v-sheet>

                <!-- Action toolbar (hidden on print) -->
                <v-row v-if="reportData.length > 0" class="d-print-none py-2 mt-2" justify="center">
                  <v-col cols="12" class="d-flex justify-center gap-2 flex-wrap">
                    <v-btn color="success" prepend-icon="mdi-printer" @click="printReport">Print</v-btn>
                    <!-- <v-btn variant="outlined" color="success" prepend-icon="mdi-download" @click="exportToExcel">EXCEL</v-btn>
                    <v-btn variant="outlined" color="red" prepend-icon="mdi-download" @click="exportToPDF">PDF</v-btn> -->
                  </v-col>
                </v-row>

                <!-- Loading -->
                <v-card v-if="loading" class="mt-4">
                  <v-card-text class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="64" />
                    <p class="mt-4 text-h6">Generating report...</p>
                  </v-card-text>
                </v-card>

                <!-- Error -->
                <v-card v-if="error" class="mt-4">
                  <v-card-text class="text-center py-8">
                    <v-icon color="error" size="64">mdi-alert-circle</v-icon>
                    <p class="mt-4 text-h6 text-error">{{ error }}</p>
                    <v-btn color="primary" @click="loadReport" class="mt-2">Try Again</v-btn>
                  </v-card-text>
                </v-card>

                <!-- No data -->
                <v-card v-if="!loading && !error && filters.container_no && lastFetched && reportData.length === 0" class="mt-4">
                  <v-card-text class="text-center py-8 text-medium-emphasis">
                    No consignment data found for this Container No and date range.
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue'
import { useStore } from 'vuex'
import { accountsService } from '@/services/accountsService'
import convertNumberToWords from '@/plugins/numberToWord'
import formattedDate from '@/plugins/dateFormatter'
import formatDateView from '@/plugins/dateFormatView'

const instance = getCurrentInstance()
const store = useStore()

const panel = ref([0])
const loading = ref(false)
const error = ref(null)
const lastFetched = ref(false)
const rawData = ref([])
const rawExpenses = ref([])

const filters = ref({
  from_date: null,
  to_date: null,
  container_no: null,
})

const getInvSalesInvoiceDeliveryList = computed(() => store.getters.getInvSalesInvoiceDeliveryList || [])

const containerNoOptions = computed(() => {
  const list = getInvSalesInvoiceDeliveryList.value
  const seen = new Set()
  return list
    .filter((item) => item.container_no != null && String(item.container_no).trim() !== '')
    .filter((item) => {
      const key = String(item.container_no).trim()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
})

const reportData = computed(() => rawData.value)

const particularsList = computed(() => {
  return (rawExpenses.value || []).map((e) => {
    const head = e.account_head || e.accountHead
    return {
      voucher_date: e.voucher_date,
      particulars: head?.name || e.remarks || '—',
      amount: e.amount ?? 0,
    }
  })
})

const totals = computed(() => {
  const data = rawData.value
  const totalBill = data.reduce((s, r) => s + (parseFloat(r.total) || 0), 0)
  const totalReceive = data.reduce((s, r) => s + (parseFloat(r.paid) || 0), 0)
  const totalDiscount = data.reduce((s, r) => s + (parseFloat(r.discount_amount) || 0), 0)
  const totalDue = data.reduce((s, r) => s + (parseFloat(r.due) || 0), 0)
  const totalExpenses = particularsList.value.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)
  const profitLoss = totalBill - totalExpenses - totalDiscount
  return {
    totalBill,
    totalReceive,
    totalDiscount,
    totalDue,
    totalExpenses,
    profitLoss,
  }
})

const consignmentNo = computed(() => {
  const data = rawData.value
  if (!data.length) return '—'
  const first = data[0].container_no
  const allSame = data.every((r) => (r.container_no || '') === (first || ''))
  return allSame && first ? first : 'Multiple'
})

const consignmentType = computed(() => {
  return 'SEA-LCL'
})

const amountInWords = computed(() => {
  const amt = totals.value.totalBill
  if (amt == null || isNaN(amt)) return '—'
  return convertNumberToWords(amt) || '—'
})

function formatAmount(val) {
  const n = parseFloat(val)
  if (isNaN(n)) return '0.00'
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
}

async function loadReport() {
  if (!filters.value.container_no || !String(filters.value.container_no).trim()) {
    error.value = 'Please select Container No.'
    return
  }
  loading.value = true
  error.value = null
  lastFetched.value = false
  try {
    const params = {
      from_date: formattedDate(filters.value.from_date) || undefined,
      to_date: formattedDate(filters.value.to_date) || undefined,
      container_no: String(filters.value.container_no).trim(),
    }
    const response = await accountsService.getConsignmentWiseProfitLoss(params)
    if (response.success && response.data) {
      rawData.value = Array.isArray(response.data.invoices) ? response.data.invoices : []
      rawExpenses.value = Array.isArray(response.data.expenses) ? response.data.expenses : []
    } else {
      rawData.value = []
      rawExpenses.value = []
    }
    lastFetched.value = true
  } catch (err) {
    console.error('Consignment wise profit/loss:', err)
    error.value = err.response?.data?.message || 'Failed to load report.'
    rawData.value = []
    rawExpenses.value = []
  } finally {
    loading.value = false
  }
}

function resetFilters() {
  filters.value = { from_date: null, to_date: null, container_no: null }
  rawData.value = []
  rawExpenses.value = []
  error.value = null
  lastFetched.value = false
}

onMounted(() => {
  if (!store.getters.getInvSalesInvoiceDeliveryList?.length) {
    store.dispatch('getInvSalesInvoiceDeliveryList', {})
  }
})

function exportToExcel() {
  if (!rawData.value.length) return
  const data = [
    ['Consignment Wise Profit/Loss Report'],
    
    [],
    ['SL', 'SO No', 'Bill No', 'Container No', 'Buyer Name', 'Bill Amount', 'Receive Amount', 'Discount Amount', 'Due Amount'],
  ]
  reportData.value.forEach((row, i) => {
    data.push([
      i + 1,
      (row.sale_order || row.saleOrder)?.code || '—',
      row.code || '—',
      row.container_no || '—',
      row.customer?.customer_name || row.customer?.customer_owner_name || '—',
      formatAmount(row.total),
      formatAmount(row.paid),
      formatAmount(row.discount_amount),
      formatAmount(row.due),
    ])
  })
  data.push(['Total', '', '', '', '', formatAmount(totals.value.totalBill), formatAmount(totals.value.totalReceive), formatAmount(totals.value.totalDiscount), formatAmount(totals.value.totalDue)])
  data.push([])
  if (particularsList.value.length) {
    data.push(['Date', 'Particulars', 'Amount'])
    particularsList.value.forEach((p) => {
      data.push([formatDateView(p.voucher_date), p.particulars, formatAmount(p.amount)])
    })
    data.push(['', 'Total', formatAmount(totals.value.totalExpenses)])
  }
  data.push([])
  data.push(['Total Bill Amount', formatAmount(totals.value.totalBill)])
  data.push(['(-) Expenses Amount', formatAmount(totals.value.totalExpenses)])
  data.push(['(-) Discount Amount', formatAmount(totals.value.totalDiscount)])
  data.push(['Profit/Loss Amount', formatAmount(totals.value.profitLoss)])
  data.push(['Total Due Amount', formatAmount(totals.value.totalDue)])
  instance.proxy.$exportTableToExcel(data, 'consignment_wise_profit_loss_' + instance.proxy.$fileDate())
}

function exportToPDF() {
  if (!rawData.value.length) return
  const columns = ['SL', 'SO No', 'Bill No', 'Container No', 'Buyer Name', 'Bill Amount', 'Receive Amount', 'Discount', 'Due']
  const rows = reportData.value.map((row, i) => [
    i + 1,
    (row.sale_order || row.saleOrder)?.code || '—',
    row.code || '—',
    row.container_no || '—',
    (row.customer?.customer_name || row.customer?.customer_owner_name || '—').substring(0, 25),
    formatAmount(row.total),
    formatAmount(row.paid),
    formatAmount(row.discount_amount),
    formatAmount(row.due),
  ])
  rows.push(['Total', '', '', '', '', formatAmount(totals.value.totalBill), formatAmount(totals.value.totalReceive), formatAmount(totals.value.totalDiscount), formatAmount(totals.value.totalDue)])
  instance.proxy.$exportTableToPDF(
    columns,
    rows,
    `Consignment Wise Profit/Loss - ${consignmentNo.value}`,
    'consignment_wise_profit_loss_' + instance.proxy.$fileDate(),
    'landscape',
    { styles: { fontSize: 8 }, headStyles: { fontSize: 9, fillColor: [66, 139, 202] }, margin: { top: 30 } }
  )
}

function esc(v) {
  if (v === null || v === undefined) return ''
  return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

async function printReport() {
  if (!rawData.value.length) return
  const rows = reportData.value
  const particulars = particularsList.value
  const t = totals.value
  const company = store.getters.getCurrentCompany || {}
  const fileServerUrl = (store.getters.getFileServerUrl || '').replace(/\/$/, '')
  const logoSrc = company.logo_thumb
    ? `${fileServerUrl}/${company.logo_thumb}`
    : `${fileServerUrl}/uploads/demo_images/user.png`
  const companyName = company.name || company.company_name || ''
  const companyAddress = company.address || ''
  const rowsHtml = rows
    .map(
      (row, i) => `
    <tr>
      <td class="text-center">${i + 1}</td>
      <td>${esc((row.sale_order || row.saleOrder)?.code || '—')}</td>
      <td>${esc(row.code || '—')}</td>
      <td>${esc(row.container_no || '—')}</td>
      <td>${esc(row.customer?.customer_name || row.customer?.customer_owner_name || '—')}</td>
      <td class="text-end">${formatAmount(row.total)}</td>
      <td class="text-end">${formatAmount(row.paid)}</td>
      <td class="text-end">${formatAmount(row.discount_amount)}</td>
      <td class="text-end">${formatAmount(row.due)}</td>
    </tr>`
    )
    .join('')
  const particularsRowsHtml =
    particulars
      .map(
        (p) => `
    <tr>
      <td>${esc(formatDateView(p.voucher_date))}</td>
      <td>${esc(p.particulars)}</td>
      <td class="text-end">${formatAmount(p.amount)}</td>
    </tr>`
      )
      .join('') +
    `
    <tr class="total-row">
      <td></td>
      <td class="font-weight-bold text-end">Total</td>
      <td class="text-end font-weight-bold">${formatAmount(t.totalExpenses)}</td>
    </tr>`
  const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>Consignment Wise Profit/Loss Report - ${esc(consignmentNo.value)}</title>
<link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet"/>
<style>
  body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #111; }
  .paper { max-width: 280mm; margin: 10px auto; background: #fff; padding: 18px; box-sizing: border-box; }
  .report-title-box { border: 1px solid #000; padding: 12px; }
  .report-title { margin: 0; font-size: 1.1rem; }
  .dep-table { width: 100%; border-collapse: collapse; font-size: 14px; margin-bottom: 20px; }
  .dep-table th, .dep-table td { border: 1px solid #000; padding: 6px 8px; vertical-align: top; }
  .dep-table th { background-color: #e0e0e0; font-weight: 600; }
  .dep-table .total-row { font-weight: bold; background: #f0f0f0; }
  .dep-table .total-line-row td { border-top: 2px solid #333; padding-top: 8px; font-weight: bold; }
  .text-end { text-align: right; }
  .font-weight-bold { font-weight: bold; }
  .signature-section { margin-top: 24px; }
  .text-center { text-align: center; }
  .print-header, .print-footer { display: none; }
  @media print {
    body * { visibility: hidden; }
    #printable, #printable * { visibility: visible; }
    .print-header, .print-header *, .print-footer, .print-footer * { visibility: visible !important; }
    #printable { position: absolute; left: 0; top: 0; width: 100%; padding: 8px; }
    @page { size: A4 portrait; margin: 1.2cm 0.5cm; }
    .print-header { display: block !important; position: fixed; top: 0; left: 0; right: 0; z-index: 9999; background: #fff; padding: 8px 12px; border-bottom: 1px solid #ddd; font-size: 12px; }
    .print-footer { display: block !important; position: fixed; bottom: 0; left: 0; right: 0; z-index: 9999; background: #fff; padding: 8px 12px; border-top: 1px solid #ddd; font-size: 11px; text-align: center; }
    #printable { padding-top: 60px; padding-bottom: 80px; }
  }
</style>
</head>
<body>
<div class="print-header">
  <div class="d-flex justify-content-between align-items-center">
    <div><img src="${logoSrc}" alt="Logo" style="max-height: 36px; width: auto;" /></div>
    <div class="text-end">
      <strong class="text-uppercase">${esc(companyName)}</strong>
      <br/><span style="font-size: 10px;">${esc(companyAddress)}</span>
    </div>
  </div>
  
</div>
<div class="print-footer">
  <hr class="my-1" style="border-color: #ccc;"/>
  UAE Address: Dnata Building Freight Gate 5 Building, Office no 210, 2nd Floor, Block E, Dubai Airport Free Zone Authorities Premises, DAFZA, Dubai.<br/>
  Cell: +971 507155639 &amp; +971 566491905<br/>
  Dhaka Office: House Name: Sahnaz Sultana house no: 14 Road no: 32 Lift: 6-a Gulshan-1, Dhaka-1212, Bangladesh.<br/>
  Cell: +88 01898461015 &amp; +88 01330456380
</div>
<div id="printable" class="paper">
  <div class="report-title-box mb-3">
    <h2 class="report-title">Consignment Wise Profit/Loss Report</h2>
    <div style="display:flex; justify-content: space-between; margin-top: 8px;">
     
     
    </div>
  </div>

  <table class="dep-table">
    <thead>
      <tr>
        <th class="text-center" style="width:40px">SL</th>
        <th>SO No</th>
        <th>Bill No</th>
        <th>Container No</th>
        <th>Buyer Name</th>
        <th class="text-end">Bill Amount</th>
        <th class="text-end">Receive Amount</th>
        <th class="text-end">Discount Amount</th>
        <th class="text-end">Due Amount</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
      <tr class="total-row">
        <td></td><td></td><td></td><td></td>
        <td class="font-weight-bold">Total</td>
        <td class="text-end">${formatAmount(t.totalBill)}</td>
        <td class="text-end">${formatAmount(t.totalReceive)}</td>
        <td class="text-end">${formatAmount(t.totalDiscount)}</td>
        <td class="text-end">${formatAmount(t.totalDue)}</td>
      </tr>
    </tbody>
  </table>

  <table class="dep-table">
    <thead>
      <tr>
        <th style="width:120px">Date</th>
        <th>Particulars</th>
        <th class="text-end" style="width:140px">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${particularsRowsHtml}
      <tr><td></td><td class="text-end">Total Bill Amount</td><td class="text-end font-weight-bold">${formatAmount(t.totalBill)}</td></tr>
      <tr><td></td><td class="text-end">(-)Expenses Amount</td><td class="text-end font-weight-bold">${formatAmount(t.totalExpenses)}</td></tr>
      <tr><td></td><td class="text-end">(-)Discount Amount</td><td class="text-end font-weight-bold">${formatAmount(t.totalDiscount)}</td></tr>
      <tr><td></td><td class="text-end">Profit/Loss Amount</td><td class="text-end font-weight-bold">${formatAmount(t.profitLoss)}</td></tr>
      <tr><td></td><td class="text-end">Total Due Amount</td><td class="text-end font-weight-bold">${formatAmount(t.totalDue)}</td></tr>
    </tbody>
  </table>

  <div class="signature-section" style="display:flex; justify-content: space-between; align-items: flex-end;">
    <div><strong>In Word:</strong> ${esc(amountInWords.value)}</div>
    <div>Authorized Signature</div>
  </div>
</div>
</body>
</html>`
  const w = window.open('', 'PRINT', `width=${screen.width},height=${screen.height}`)
  w.document.open()
  w.document.write(html)
  w.document.close()
  await new Promise((res) => setTimeout(res, 400))
  w.focus()
  w.print()
  w.close()
}

</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

.report-paper {
  background-color: white;
  color: #111;
  font-family: Arial, Helvetica, sans-serif;
  max-width: 280mm;
  width: 100%;
  min-height: 200px;
}

.report-title-box {
  border: 1px solid #000;
  padding: 12px;
}

.report-title {
  margin: 0;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.table-container {
  overflow-x: auto;
}

.dep-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.dep-table th,
.dep-table td {
  border: 1px solid #000;
  padding: 6px 8px;
  vertical-align: top;
}

.dep-table th {
  background-color: #e0e0e0;
  font-weight: 600;
}

.dep-table .total-row {
  font-weight: bold;
  background-color: #f0f0f0;
}

/* Profit/Loss row in Particulars table */
.dep-table .total-line-row td {
  border-top: 2px solid #333;
  padding-top: 8px;
  font-weight: bold;
}

.signature-section .in-word-text {
  font-size: 14px;
  text-align: left;
}

.signature-section .authorized-signature {
  font-size: 14px;
  font-weight: 500;
}

.gap-2 {
  gap: 8px;
}

/* Print styles (like salesInvoiceDeliveryInvoice) */
@media print {
  body * {
    visibility: hidden;
  }
  #printable-area,
  #printable-area * {
    visibility: visible;
  }
  #printable-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 15px;
  }
  .report-paper {
    box-shadow: none !important;
  }
  @page {
    size: A4 portrait;
    margin: 0.5cm;
  }
  .d-print-none {
    display: none !important;
  }
}
</style>
